import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CTAButton } from "./CTAButton";
import { useLang } from "../../i18n/LanguageContext";
import { useJourney } from "../../context/JourneyContext";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.25 + i * 0.1 },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const { journey } = useJourney();
  const isLocal = journey === "local";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const badge = isLocal ? t.heroLocal.badge : t.hero.badge;
  const sub = isLocal ? t.heroLocal.sub : t.hero.sub;
  const caption = isLocal ? t.heroLocal.caption : t.hero.caption;
  const lines = isLocal
    ? [t.heroLocal.line1, t.heroLocal.line2, t.heroLocal.line3]
    : [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section ref={ref} className="relative pt-32 sm:pt-40 pb-16 sm:pb-24 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-terra" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold">
              {badge}
            </span>
          </motion.div>

          <h1 className="font-display text-[var(--ink)] font-semibold tracking-tight leading-[1.05] pt-1
                         text-[2.6rem] sm:text-6xl lg:text-[4.6rem]">
            {lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--muted-ink)]"
          >
            {sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.68 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <CTAButton testId="hero-cta" label={t.hero.cta} />
            <span className="text-sm text-[var(--muted-ink)]">{t.hero.ctaNote}</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[340px] sm:h-[460px] lg:h-[560px] overflow-hidden rounded-[4px] border border-[var(--hairline)]"
          >
            <motion.img
              src="https://images.pexels.com/photos/19899369/pexels-photo-19899369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
              alt={isLocal ? "Aerial view of Fiji's islands and turquoise ocean, where Fiji citizens set up their businesses" : "Aerial view of Fiji's islands and turquoise ocean, where foreign investors set up their businesses"}
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-[122%] w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-green/10" />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-green/70 to-transparent">
              <p className="text-cream text-sm font-medium tracking-wide">{caption}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
